import { ref, shallowRef, triggerRef } from 'vue';
import type { SignalingMessage, Participant, QualityPreset } from '../types';
import { useWhepPlayer } from './useWhepPlayer';
import { isDiscordHost, getSignalingUrl } from '../net';

export const QUALITY_PRESETS: QualityPreset[] = [
  { id: '720p60', name: '720p @ 60fps (Rápido & Leve - 8 Mbps)', width: 1280, height: 720, fps: 60, maxBitrateKbps: 8000 },
  { id: '1080p60', name: '1080p @ 60fps (Twitch & Discord Pro - 14 Mbps)', width: 1920, height: 1080, fps: 60, maxBitrateKbps: 14000 },
  { id: '1080p60ultra', name: '1080p @ 60fps (Ultra Qualidade Master - 20 Mbps)', width: 1920, height: 1080, fps: 60, maxBitrateKbps: 20000 },
  { id: '1440p60', name: '1440p 2K @ 60fps (Quad HD Crystal - 28 Mbps)', width: 2560, height: 1440, fps: 60, maxBitrateKbps: 28000 },
  { id: 'source', name: 'Original / Fonte Nativa (Sem Limites - 40 Mbps)', width: 0, height: 0, fps: 60, maxBitrateKbps: 40000 },
];

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
    { urls: 'stun:stun.cloudflare.com:3478' },
    { urls: 'stun:global.stun.twilio.com:3478' },
    {
      urls: [
        'turn:openrelay.metered.ca:80',
        'turn:openrelay.metered.ca:443',
        'turn:openrelay.metered.ca:443?transport=tcp',
      ],
      username: 'openrelay',
      credential: 'openrelay',
    },
  ],
  iceCandidatePoolSize: 4,
  bundlePolicy: 'max-bundle',
  rtcpMuxPolicy: 'require',
};

const isWebRTCSupported =
  typeof window !== 'undefined' &&
  typeof (window as any).RTCPeerConnection === 'function';

function getAvatarUrl(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash + id.charCodeAt(i)) % 5;
  }
  return `https://cdn.discordapp.com/embed/avatars/${hash}.png`;
}

export function useWebRTC() {
  const isStreaming = ref(false);
  const isReceiving = ref(false);
  const currentStreamer = ref<Participant | null>(null);
  const participants = ref<Participant[]>([]);
  const selectedQuality = ref<QualityPreset>(QUALITY_PRESETS[1]);
  const connectionState = ref<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  const lastError = ref<string | null>(null);
  const webrtcStatus = ref<string>('Aguardando transmissão...');

  const localStream = shallowRef<MediaStream | null>(null);
  const remoteStream = shallowRef<MediaStream | null>(null);
  const streamVideoUrl = ref<string | null>(null);

  let ws: WebSocket | null = null;
  const peerConnections = new Map<string, RTCPeerConnection>();
  const pendingCandidates = new Map<string, RTCIceCandidateInit[]>();

  let myUserId = '';
  let myUserName = '';
  let myRoomId = '';
  let localVideoElementForFileStream: HTMLVideoElement | null = null;

  // MediaRecorder (Transmissor) e MediaSource (Espectador)
  let mediaRecorder: MediaRecorder | null = null;
  let mediaSource: MediaSource | null = null;
  let sourceBuffer: SourceBuffer | null = null;
  let chunkQueue: ArrayBuffer[] = [];
  let currentMimeType = 'video/webm; codecs="vp8, opus"';
  // Flag para evitar re-inicialização enquanto aguarda sourceopen
  // Sem isso, 30 chunks chegando antes do sourceopen reinicializam o MediaSource 30 vezes,
  // limpando a queue em cada vez e causando tela preta permanente
  let isInitializing = false;

  // --- Gerenciamento de MediaSource (MSE) para reprodução via WebSocket ---
  function initMediaSource(preferredMime?: string, forceReset = false) {
    if (!forceReset && mediaSource && mediaSource.readyState === 'open' && sourceBuffer) {
      console.log('[Nexia Stream] MediaSource já está ativo e aberto. Mantendo.');
      return;
    }

    cleanupMediaSource();

    if (typeof MediaSource === 'undefined') {
      console.warn('[Nexia Stream] MediaSource não é suportado neste ambiente.');
      return;
    }

    let mime = preferredMime || 'video/webm; codecs="vp8, opus"';
    if (!MediaSource.isTypeSupported(mime)) {
      if (MediaSource.isTypeSupported('video/webm; codecs="vp8"')) {
        mime = 'video/webm; codecs="vp8"';
      } else if (MediaSource.isTypeSupported('video/webm')) {
        mime = 'video/webm';
      }
    }

    currentMimeType = mime;
    console.log('[Nexia Stream] Inicializando MediaSource com MIME:', mime);

    try {
      mediaSource = new MediaSource();
      const objectUrl = URL.createObjectURL(mediaSource);
      streamVideoUrl.value = objectUrl;

      mediaSource.addEventListener('sourceopen', () => {
        isInitializing = false; // Pipeline pronto — para de bloquear chunks
        if (!mediaSource || mediaSource.readyState !== 'open') return;
        try {
          sourceBuffer = mediaSource.addSourceBuffer(currentMimeType);
          sourceBuffer.mode = 'segments';

          sourceBuffer.addEventListener('updateend', () => {
            processChunkQueue();
          });

          sourceBuffer.addEventListener('error', (e) => {
            console.warn('[Nexia Stream] Erro no SourceBuffer:', e);
            // Tentar recuperar reinicializando a fila
            chunkQueue = [];
          });

          processChunkQueue();
        } catch (err) {
          console.error('[Nexia Stream] Erro ao adicionar SourceBuffer:', err);
        }
      });

      mediaSource.addEventListener('error', (e) => {
        console.warn('[Nexia Stream] Erro no MediaSource:', e);
      });
    } catch (err) {
      console.error('[Nexia Stream] Falha ao instanciar MediaSource:', err);
    }
  }

  function handleBinaryChunk(buffer: ArrayBuffer) {
    // Só ignora chunks do WebSocket se o WebRTC P2P estiver REALMENTE conectado e transmitindo
    const hasWebRtcConnected = Array.from(peerConnections.values()).some(
      (pc) => pc.connectionState === 'connected'
    );
    if (hasWebRtcConnected && remoteStream.value) {
      return;
    }

    // Se está inicializando (sourceopen ainda não disparou), apenas enfileira.
    if (isInitializing) {
      chunkQueue.push(buffer);
      return;
    }

    if (!mediaSource || mediaSource.readyState === 'closed') {
      isInitializing = true;
      initMediaSource(currentMimeType);
      chunkQueue.push(buffer);
      return;
    }

    chunkQueue.push(buffer);
    processChunkQueue();
  }

  function processChunkQueue() {
    if (!sourceBuffer || sourceBuffer.updating || chunkQueue.length === 0) return;
    if (!mediaSource || mediaSource.readyState !== 'open') return;

    const nextChunk = chunkQueue.shift();
    if (!nextChunk) return;

    try {
      sourceBuffer.appendBuffer(nextChunk);
    } catch (err: any) {
      console.warn('[Nexia Stream] Erro ao chamar appendBuffer:', err);
      if (err.name === 'QuotaExceededError' && sourceBuffer && sourceBuffer.buffered.length > 0) {
        // Remove chunks antigos para liberar espaço
        try {
          const start = sourceBuffer.buffered.start(0);
          const end = sourceBuffer.buffered.end(sourceBuffer.buffered.length - 1);
          if (end - start > 20) {
            sourceBuffer.remove(start, Math.max(start, end - 10));
          }
        } catch (e) {}
      } else if (err.name === 'InvalidStateError') {
        console.warn('[Nexia Stream] SourceBuffer ocupado ou em estado transiente, limpando fila pendente.');
        chunkQueue = [];
      }
    }
  }

  function cleanupMediaSource() {
    isInitializing = false;
    chunkQueue = [];
    if (sourceBuffer) {
      try {
        if (sourceBuffer.updating) {
          sourceBuffer.abort();
        }
        if (mediaSource && mediaSource.readyState === 'open') {
          mediaSource.removeSourceBuffer(sourceBuffer);
        }
      } catch (e) {}
      sourceBuffer = null;
    }

    if (mediaSource) {
      try {
        if (mediaSource.readyState === 'open') {
          mediaSource.endOfStream();
        }
      } catch (e) {}
      mediaSource = null;
    }

    if (streamVideoUrl.value) {
      try {
        URL.revokeObjectURL(streamVideoUrl.value);
      } catch (e) {}
      streamVideoUrl.value = null;
    }
  }

  // --- Gerenciamento de MediaRecorder (Transmissão via WebSocket) ---
  function startMediaRecorder(stream: MediaStream) {
    if (typeof MediaRecorder === 'undefined') {
      console.warn('[Nexia Stream] MediaRecorder não disponível.');
      return;
    }

    const hasAudio = stream.getAudioTracks().length > 0;
    let mimeType = 'video/webm; codecs=vp8';
    if (hasAudio && MediaRecorder.isTypeSupported('video/webm; codecs=vp8,opus')) {
      mimeType = 'video/webm; codecs=vp8,opus';
    } else if (MediaRecorder.isTypeSupported('video/webm; codecs=vp8')) {
      mimeType = 'video/webm; codecs=vp8';
    } else if (MediaRecorder.isTypeSupported('video/webm')) {
      mimeType = 'video/webm';
    }

    const bitrate = (selectedQuality.value.maxBitrateKbps || 4500) * 1000;
    const options: MediaRecorderOptions = {
      mimeType,
      videoBitsPerSecond: bitrate,
      audioBitsPerSecond: 128000,
    };

    try {
      (options as any).videoKeyFrameIntervalDuration = 1000;
    } catch (e) {}

    try {
      mediaRecorder = new MediaRecorder(stream, options);

      mediaRecorder.ondataavailable = async (event) => {
        if (event.data && event.data.size > 0 && ws && ws.readyState === WebSocket.OPEN) {
          // Se houver peer conectado diretamente via WebRTC P2P, NÃO sobrecarrega os servidores
          const hasConnectedPeers = Array.from(peerConnections.values()).some(
            (pc) => pc.connectionState === 'connected'
          );
          if (hasConnectedPeers) {
            return;
          }

          try {
            const arrayBuffer = await event.data.arrayBuffer();
            ws.send(arrayBuffer);
          } catch (err) {
            console.warn('[Nexia Stream] Erro ao enviar chunk binário:', err);
          }
        }
      };

      // Fatias ultra-rápidas de 100ms para latência em tempo real
      mediaRecorder.start(100);
      console.log('[Nexia Stream] MediaRecorder transmitindo com sucesso via WebSocket:', mimeType);

      sendSignalingMessage({
        type: 'stream-started',
        roomId: myRoomId,
        senderId: myUserId,
        payload: { name: myUserName, mimeType },
      });
    } catch (err) {
      console.warn('[Nexia Stream] Falha ao iniciar MediaRecorder:', err);
    }
  }

  let lastSignalingUrl = '';
  let reconnectTimer: number | undefined;
  let reconnectAttempts = 0;

  function scheduleReconnect() {
    if (reconnectTimer) return;
    reconnectAttempts++;
    const delay = Math.min(reconnectAttempts * 1000, 3500);
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = undefined;
      if (connectionState.value !== 'connected' && myRoomId && myUserId) {
        let targetUrl = lastSignalingUrl || getSignalingUrl();
        if (!isDiscordHost()) {
          const fallbackUrl = getSignalingUrl();
          targetUrl = reconnectAttempts % 2 === 0 ? fallbackUrl : (lastSignalingUrl || fallbackUrl);
        }
        console.log(`[Nexia WebRTC] Tentando reconectar (tentativa ${reconnectAttempts}) em ${targetUrl}...`);
        connectSignaling(targetUrl, myRoomId, myUserId, myUserName);
      }
    }, delay);
  }

  let pingTimer: number | undefined;

  function startPingHeartbeat() {
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = window.setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping', roomId: myRoomId, senderId: myUserId }));
      }
    }, 10000);
  }

  function stopPingHeartbeat() {
    if (pingTimer) {
      clearInterval(pingTimer);
      pingTimer = undefined;
    }
  }

  // Conectar ao servidor de sinalização WebSocket
  function connectSignaling(signalingUrl: string, roomId: string, userId: string, userName: string) {
    myUserId = userId;
    myUserName = userName;
    myRoomId = roomId;
    lastSignalingUrl = signalingUrl;

    connectionState.value = 'connecting';
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const finalUrl = signalingUrl.startsWith('http')
      ? signalingUrl.replace(/^http/, 'ws')
      : signalingUrl.startsWith('ws')
      ? signalingUrl
      : `${protocol}//${window.location.host}${signalingUrl}`;

    console.log('[Nexia WebRTC] Conectando ao Signaling:', `${finalUrl}?room=${roomId}&user=${userId}&name=${encodeURIComponent(userName)}`);

    try {
      if (ws) {
        stopPingHeartbeat();
        try { ws.close(); } catch (e) {}
      }

      ws = new WebSocket(`${finalUrl}?room=${roomId}&user=${userId}&name=${encodeURIComponent(userName)}`);
      ws.binaryType = 'arraybuffer';

      ws.onopen = () => {
        reconnectAttempts = 0;
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = undefined;
        }
        connectionState.value = 'connected';
        startPingHeartbeat();
        console.log('[Nexia WebRTC] Conectado ao servidor de sinalização!');

        // Notificar entrada na sala
        sendSignalingMessage({
          type: 'join',
          roomId,
          senderId: userId,
          payload: { name: userName },
        });
      };

      ws.onmessage = async (event) => {
        // Se for dado binário (chunk de vídeo/áudio transmitido via WebSocket)
        if (event.data instanceof ArrayBuffer) {
          handleBinaryChunk(event.data);
          return;
        }

        if (event.data instanceof Blob) {
          const buf = await event.data.arrayBuffer();
          handleBinaryChunk(buf);
          return;
        }

        try {
          const msg: SignalingMessage = JSON.parse(event.data);
          if (msg.type === 'pong' as any) {
            return;
          }
          await handleSignalingMessage(msg);
        } catch (err) {
          console.error('[Nexia WebRTC] Erro ao processar mensagem do servidor:', err);
        }
      };

      ws.onerror = (err) => {
        console.warn('[Nexia WebRTC] Erro no WebSocket de sinalização:', err);
        connectionState.value = 'error';
        stopPingHeartbeat();
        scheduleReconnect();
      };

      ws.onclose = () => {
        console.log('[Nexia WebRTC] WebSocket desconectado.');
        connectionState.value = 'disconnected';
        stopPingHeartbeat();
        scheduleReconnect();
      };
    } catch (err) {
      console.error('[Nexia WebRTC] Falha ao instanciar WebSocket:', err);
      connectionState.value = 'error';
      stopPingHeartbeat();
      scheduleReconnect();
    }
  }

  function sendSignalingMessage(msg: SignalingMessage) {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }

  const whepPlayer = useWhepPlayer(sendSignalingMessage, (reason: string) => {
    console.warn(`[Nexia WebRTC] Fallback WHEP disparado: ${reason}`);
    webrtcStatus.value = 'Conectando via Relay (WebSocket)...';
    if (currentStreamer.value) {
      initMediaSource((currentStreamer.value as any)?.mimeType, true);
      sendSignalingMessage({
        type: 'request-stream',
        roomId: myRoomId,
        senderId: myUserId,
        targetId: currentStreamer.value.id,
      });
    }
  });

  async function handleSignalingMessage(msg: SignalingMessage) {
    if (msg.senderId === myUserId) return; // ignorar mensagens próprias

    console.log(`[Nexia WebRTC] Mensagem [${msg.type}] de ${msg.senderId}`, msg);

    switch (msg.type) {
      // Estado inicial da sala recebido do servidor
      case 'room-state': {
        const { peers, streamer, viewers } = msg.payload || {};
        if (Array.isArray(peers)) {
          peers.forEach((p: { id: string; name: string }) => {
            addParticipant(p.id, p.name, false);
          });
        }

        if (viewers) {
          whepPlayer.handleViewersUpdate(viewers);
        }

        if (streamer) {
          const isMe = streamer.id === myUserId || (streamer.name && streamer.name.toLowerCase() === myUserName.toLowerCase());
          console.log('[Nexia WebRTC] Transmissor ativo na sala:', streamer, 'isMe:', isMe);
          currentStreamer.value = {
            id: streamer.id,
            name: streamer.name,
            avatarUrl: getAvatarUrl(streamer.id),
            isStreaming: true,
            isSelf: isMe,
          };
          updateParticipantStreaming(streamer.id, true);

          if (!isMe && !isStreaming.value) {
            isReceiving.value = true;
            webrtcStatus.value = `Conectando ao stream ao vivo de ${streamer.name}...`;

            if (streamer.transport === 'whep-local') {
              console.log('[Nexia WebRTC] Conectando via WHEP player de alto desempenho...');
              whepPlayer.startPlaying(myRoomId, myUserId, streamer.id);
            } else {
              initMediaSource(streamer.mimeType);
              sendSignalingMessage({
                type: 'request-stream',
                roomId: myRoomId,
                senderId: myUserId,
                targetId: streamer.id,
              });
            }
          } else {
            console.log('[Nexia WebRTC] Eu sou o transmissor ativo. Não iniciando player de espectador.');
          }
        }
        break;
      }

      case 'join':
      case 'peer-joined': {
        const peerName = msg.payload?.name || `Amigo (${msg.senderId.slice(-4)})`;
        addParticipant(msg.senderId, peerName, false);

        // Se eu sou o transmissor, enviar anúncio e oferta para o novo amigo
        if (isStreaming.value && localStream.value) {
          console.log(`[Nexia WebRTC] Novo membro na sala (${msg.senderId}). Enviando anúncio...`);
          sendSignalingMessage({
            type: 'stream-started',
            roomId: myRoomId,
            senderId: myUserId,
            targetId: msg.senderId,
            payload: { name: myUserName, mimeType: currentMimeType },
          });

          if (isWebRTCSupported) {
            await createAndSendOffer(msg.senderId);
          }
        }
        break;
      }

      case 'peer-left': {
        removeParticipant(msg.senderId);
        closePeerConnection(msg.senderId);
        if (currentStreamer.value?.id === msg.senderId) {
          console.log('[Nexia WebRTC] Transmissor saiu da sala. Encerrando reprodução.');
          stopReceiving();
        }
        break;
      }

      case 'stream-blocked': {
        const streamerName = msg.payload?.streamerName || 'Outro usuário';
        console.warn(`[Nexia WebRTC] Transmissão bloqueada: ${streamerName} já está transmitindo.`);
        lastError.value = `Não é possível transmitir: ${streamerName} já está transmitindo tela nesta chamada. Apenas 1 pessoa pode transmitir por vez.`;
        webrtcStatus.value = `Bloqueado: ${streamerName} está ao vivo.`;
        if (isStreaming.value) {
          stopStreaming();
        }
        break;
      }

      case 'stream-started': {
        const streamerName = msg.payload?.name || 'Amigo';
        const mimeType = msg.payload?.mimeType;
        const isMe = msg.senderId === myUserId || (streamerName && streamerName.toLowerCase() === myUserName.toLowerCase());

        addParticipant(msg.senderId, streamerName, isMe);
        updateParticipantStreaming(msg.senderId, true);

        currentStreamer.value = {
          id: msg.senderId,
          name: streamerName,
          avatarUrl: getAvatarUrl(msg.senderId),
          isStreaming: true,
          isSelf: isMe,
        };

        console.log('[Nexia WebRTC] Transmissão iniciada por:', currentStreamer.value, 'isMe:', isMe);

        // Se NÃO for eu mesmo, inicia recepção de espectador
        if (!isMe && !isStreaming.value) {
          isReceiving.value = true;
          webrtcStatus.value = `Recebendo stream ao vivo de ${streamerName}...`;

          if (msg.payload?.transport === 'whep-local') {
            console.log('[Nexia WebRTC] Conectando via WHEP player de alto desempenho...');
            whepPlayer.startPlaying(myRoomId, myUserId, msg.senderId);
          } else {
            initMediaSource(mimeType, true);
            sendSignalingMessage({
              type: 'request-stream',
              roomId: myRoomId,
              senderId: myUserId,
              targetId: msg.senderId,
            });
          }
        } else {
          console.log('[Nexia WebRTC] Eu sou o transmissor da live. Mantendo painel do streamer.');
        }
        break;
      }

      case 'stream-stopped': {
        console.log('[Nexia WebRTC] Transmissão encerrada recebida do servidor.');
        webrtcStatus.value = 'Transmissão encerrada.';
        if (currentStreamer.value) {
          updateParticipantStreaming(currentStreamer.value.id, false);
        }
        whepPlayer.stopPlaying();
        stopReceiving();
        break;
      }

      case 'whep-answer': {
        console.log('[Nexia WebRTC] Resposta WHEP recebida:', msg.payload?.requestId);
        await whepPlayer.handleAnswer(msg.payload?.requestId, msg.payload?.sdp);
        break;
      }

      case 'whep-error': {
        console.warn('[Nexia WebRTC] Erro WHEP recebido:', msg.payload);
        whepPlayer.handleError(msg.payload?.requestId, msg.payload?.reason);
        break;
      }

      case 'stream-full': {
        console.warn('[Nexia WebRTC] Capacidade máxima atingida (stream-full):', msg.payload);
        whepPlayer.handleStreamFull(msg.payload);
        break;
      }

      case 'viewers-update': {
        whepPlayer.handleViewersUpdate(msg.payload);
        break;
      }

      case 'request-stream':
      case 'resync-stream': {
        if (isStreaming.value && localStream.value) {
          console.log(`[Nexia WebRTC] ${msg.senderId} solicitou a transmissão ou re-sync.`);
          webrtcStatus.value = `Conectando espectador (${msg.senderId.slice(-4)})...`;

          if (isWebRTCSupported) {
            await createAndSendOffer(msg.senderId, true);
          }
        }
        break;
      }

      case 'offer': {
        if (isStreaming.value) return;
        if (!isWebRTCSupported) {
          console.log('[Nexia WebRTC] WebRTC não suportado neste cliente Discord. Usando WebSocket streaming.');
          return;
        }
        console.log(`[Nexia WebRTC] Oferta SDP recebida de ${msg.senderId}`);
        await handleOffer(msg.senderId, msg.payload);
        break;
      }

      case 'answer': {
        if (!isStreaming.value) return;
        if (!isWebRTCSupported) return;
        console.log(`[Nexia WebRTC] Resposta SDP recebida de ${msg.senderId}`);
        await handleAnswer(msg.senderId, msg.payload);
        break;
      }

      case 'ice-candidate': {
        if (!isWebRTCSupported) return;
        await handleCandidate(msg.senderId, msg.payload);
        break;
      }
    }
  }

  // Gerenciamento de Peer Connection (apenas executado se suportado pelo ambiente)
  function getOrCreatePeerConnection(peerId: string): RTCPeerConnection | null {
    if (!isWebRTCSupported) {
      return null;
    }

    let pc = peerConnections.get(peerId);
    if (!pc) {
      try {
        pc = new RTCPeerConnection(ICE_SERVERS);

        pc.onicecandidate = (event) => {
          if (event.candidate) {
            const candidateData = event.candidate.toJSON
              ? event.candidate.toJSON()
              : {
                  candidate: event.candidate.candidate,
                  sdpMid: event.candidate.sdpMid,
                  sdpMLineIndex: event.candidate.sdpMLineIndex,
                  usernameFragment: event.candidate.usernameFragment,
                };

            sendSignalingMessage({
              type: 'ice-candidate',
              roomId: myRoomId,
              senderId: myUserId,
              targetId: peerId,
              payload: candidateData,
            });
          }
        };

        pc.ontrack = (event) => {
          const track = event.track;
          console.log(`[Nexia WebRTC] Track recebido de ${peerId}: kind=${track.kind}, id=${track.id}, muted=${track.muted}, readyState=${track.readyState}`);

          if (track.kind === 'audio') {
            track.enabled = true;
          } else if (track.kind === 'video') {
            track.contentHint = 'motion';
          }

          // Buffer anti-jitter de 150ms (pacing ideal Twitch-grade) para 60 FPS ultra fluidos sem micro-quedas
          if (event.receiver) {
            try {
              if ('playoutDelayHint' in event.receiver) {
                (event.receiver as any).playoutDelayHint = 0.15; // 150ms buffer (fluidez perfeita estilo Twitch)
              }
              if ('jitterBufferTarget' in event.receiver) {
                (event.receiver as any).jitterBufferTarget = 150; // 150ms target
              }
            } catch (e) {}
          }

          const incomingStream = event.streams && event.streams[0] ? event.streams[0] : null;

          const syncTracksToRemoteStream = () => {
            const trackMap = new Map<string, MediaStreamTrack>();
            if (incomingStream) {
              incomingStream.getTracks().forEach((t) => trackMap.set(t.id, t));
            }
            if (pc) {
              pc.getReceivers().forEach((r) => {
                if (r.track) trackMap.set(r.track.id, r.track);
              });
            }
            if (track) {
              trackMap.set(track.id, track);
            }

            const activeTracks = Array.from(trackMap.values());
            if (activeTracks.length > 0) {
              activeTracks.forEach((t) => {
                if (t.kind === 'audio') t.enabled = true;
              });

              // Só cria uma nova instância de MediaStream se as tracks realmente mudaram,
              // evitando destruir e recriar o pipeline de renderização do player durante a live
              const currentTracks = remoteStream.value ? remoteStream.value.getTracks() : [];
              const sameTracks = currentTracks.length === activeTracks.length &&
                currentTracks.every(t => activeTracks.some(at => at.id === t.id));

              if (!sameTracks || !remoteStream.value) {
                remoteStream.value = new MediaStream(activeTracks);
                triggerRef(remoteStream);
                console.log(`[Nexia WebRTC] remoteStream atualizado com ${activeTracks.length} tracks:`, activeTracks.map(t => `${t.kind}:${t.readyState}:muted=${t.muted}`));
              }
            }
            isReceiving.value = true;
            webrtcStatus.value = `Stream WebRTC conectado!`;
          };

          syncTracksToRemoteStream();

          if (track.kind === 'video') {
            // Solicita keyframe imediato para renderizar o primeiro quadro sem tela preta
            sendSignalingMessage({
              type: 'request-keyframe',
              roomId: myRoomId,
              senderId: myUserId,
              targetId: peerId,
            });
          }

          track.onunmute = () => {
            console.log(`[Nexia WebRTC] Track ${track.kind} (${track.id}) desmutada! Sincronizando...`);
            syncTracksToRemoteStream();
            if (track.kind === 'video') {
              sendSignalingMessage({
                type: 'request-keyframe',
                roomId: myRoomId,
                senderId: myUserId,
                targetId: peerId,
              });
            }
          };

          if (incomingStream) {
            incomingStream.onaddtrack = (e) => {
              console.log(`[Nexia WebRTC] Nova track adicionada ao incomingStream:`, e.track.kind);
              syncTracksToRemoteStream();
            };
          }

          track.onended = () => {
            console.log(`[Nexia WebRTC] Track ${track.kind} encerrada.`);
            syncTracksToRemoteStream();
          };
        };

        let disconnectTimer: number | undefined;
        let keyframeInterval: number | undefined;

        pc.onconnectionstatechange = () => {
          console.log(`[Nexia WebRTC] Conexão com ${peerId} mudou para:`, pc?.connectionState);
          if (pc?.connectionState === 'connected') {
            webrtcStatus.value = 'Conectado em alta fluidez';
            if (disconnectTimer) {
              clearTimeout(disconnectTimer);
              disconnectTimer = undefined;
            }
            // Solicita keyframe imediato ao transmissor para iniciar vídeo de imediato
            sendSignalingMessage({
              type: 'request-keyframe',
              roomId: myRoomId,
              senderId: myUserId,
              targetId: peerId,
            });
            // Aplica playoutDelayHint de 150ms (Twitch-grade jitter buffer) nos receivers
            pc?.getReceivers().forEach((r) => {
              if (r.track?.kind === 'video') {
                try {
                  if ('playoutDelayHint' in r) {
                    (r as any).playoutDelayHint = 0.15;
                  }
                  if ('jitterBufferTarget' in r) {
                    (r as any).jitterBufferTarget = 150;
                  }
                } catch (e) {}
              }
            });

            // Define prioridade máxima de framerate e bitrate sem degradação de FPS
            pc?.getSenders().forEach(async (sender) => {
              if (sender.track?.kind === 'video') {
                try {
                  const params = sender.getParameters();
                  if (params.encodings?.length) {
                    const targetBitrate = (selectedQuality.value?.maxBitrateKbps || 14000) * 1000;
                    params.encodings[0].maxBitrate = targetBitrate;
                    (params.encodings[0] as any).minBitrate = Math.max(6000, Math.floor((selectedQuality.value?.maxBitrateKbps || 14000) * 0.5)) * 1000;
                    (params.encodings[0] as any).networkPriority = 'high';
                    (params.encodings[0] as any).priority = 'high';
                    params.degradationPreference = 'maintain-framerate';
                    await sender.setParameters(params);
                  }
                } catch (e) {}
              }
            });
          } else if (pc?.connectionState === 'disconnected') {
            if (disconnectTimer) clearTimeout(disconnectTimer);
            disconnectTimer = window.setTimeout(() => {
              if (pc?.connectionState === 'disconnected' || pc?.connectionState === 'failed') {
                if (pc.restartIce) {
                  pc.restartIce();
                } else {
                  remoteStream.value = null;
                }
              }
            }, 5000);
          } else if (pc?.connectionState === 'failed') {
            if (disconnectTimer) {
              clearTimeout(disconnectTimer);
              disconnectTimer = undefined;
            }
            if (pc.restartIce) {
              pc.restartIce();
            } else {
              remoteStream.value = null;
              closePeerConnection(peerId);
            }
          } else if (pc?.connectionState === 'closed') {
            if (keyframeInterval) {
              clearInterval(keyframeInterval);
              keyframeInterval = undefined;
            }
          }
        };

        peerConnections.set(peerId, pc);
      } catch (e: any) {
        console.warn('[Nexia WebRTC] Falha ao criar RTCPeerConnection:', e);
        return null;
      }
    }
    return pc;
  }

  function closePeerConnection(peerId: string) {
    const pc = peerConnections.get(peerId);
    if (pc) {
      try {
        pc.close();
      } catch (e) {}
      peerConnections.delete(peerId);
    }
    pendingCandidates.delete(peerId);
  }

  // SDP de alta fidelidade: estéreo cristalino, taxa constante (sem código morse), DTX desligado (sem estouros)
  function patchOpusStereo(sdp?: string): string | undefined {
    if (!sdp) return sdp;
    const opusMatch = sdp.match(/a=rtpmap:(\d+)\s+opus\/48000/i);
    if (!opusMatch) return sdp;
    const opusPt = opusMatch[1];
    return sdp
      .split(/\r?\n/)
      .map(line => {
        if (line.startsWith(`a=fmtp:${opusPt}`)) {
          return `a=fmtp:${opusPt} minptime=10;useinbandfec=1;stereo=1;sprop-stereo=1;maxaveragebitrate=320000;cbr=1;usedtx=0`;
        }
        return line;
      })
      .join('\r\n');
  }

  // SDP de vídeo: aloca banda suficiente para 60 FPS contínuos evitando oscilação de congestion control
  function patchVideoBitrate(sdp?: string, bitrateKbps = 14000): string | undefined {
    if (!sdp) return sdp;
    const lines = sdp.split(/\r?\n/);
    let inVideo = false;
    const patched: string[] = [];
    const bps = bitrateKbps * 1000;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.startsWith('m=video ')) {
        inVideo = true;
        patched.push(line);
        patched.push(`b=AS:${bitrateKbps}`);
        patched.push(`b=TIAS:${bps}`);
        continue;
      } else if (line.startsWith('m=')) {
        inVideo = false;
      }

      if (inVideo && line.startsWith('a=fmtp:')) {
        if (!line.includes('x-google-min-bitrate')) {
          const minKbps = Math.max(6000, Math.floor(bitrateKbps * 0.5));
          const startKbps = Math.max(10000, Math.floor(bitrateKbps * 0.8));
          const maxKbps = Math.max(bitrateKbps, 20000);
          patched.push(`${line};x-google-min-bitrate=${minKbps};x-google-start-bitrate=${startKbps};x-google-max-bitrate=${maxKbps}`);
          continue;
        }
      }
      patched.push(line);
    }
    return patched.join('\r\n');
  }

  // Prioriza H.264 (NVENC/GPU) para latência de codificação ultrabaixa (2ms) igual à Twitch
  function prioritizeH264InSdp(sdp?: string): string | undefined {
    if (!sdp) return sdp;
    const lines = sdp.split(/\r?\n/);
    const h264Pts: string[] = [];

    for (const line of lines) {
      const match = line.match(/^a=rtpmap:(\d+)\s+H264\/90000/i);
      if (match) {
        h264Pts.push(match[1]);
      }
    }

    if (h264Pts.length === 0) return sdp;

    return lines.map(line => {
      if (line.startsWith('m=video ')) {
        const parts = line.split(' ');
        const header = parts.slice(0, 3);
        const pts = parts.slice(3);
        const remainingPts = pts.filter(pt => !h264Pts.includes(pt));
        return [...header, ...h264Pts, ...remainingPts].join(' ');
      }
      return line;
    }).join('\r\n');
  }

  function prioritizeH264Codecs(pc: RTCPeerConnection) {
    if (typeof (RTCRtpSender as any).getCapabilities === 'function') {
      try {
        const caps = (RTCRtpSender as any).getCapabilities('video');
        if (caps && caps.codecs) {
          const h264Codecs = caps.codecs.filter((c: any) => c.mimeType.toLowerCase() === 'video/h264');
          const otherCodecs = caps.codecs.filter((c: any) => c.mimeType.toLowerCase() !== 'video/h264');
          if (h264Codecs.length > 0) {
            const sorted = [...h264Codecs, ...otherCodecs];
            pc.getTransceivers().forEach(t => {
              if (t.sender.track?.kind === 'video' || t.receiver.track?.kind === 'video') {
                try {
                  t.setCodecPreferences(sorted);
                } catch (e) {}
              }
            });
          }
        }
      } catch (e) {}
    }
  }

  async function createAndSendOffer(peerId: string, force = false) {
    if (!isWebRTCSupported || !localStream.value) return;

    let pc = peerConnections.get(peerId);
    // Se a conexão já estiver 100% estabelecida e estável e NÃO for solicitação forçada de re-sync, mantém
    if (!force && pc && pc.connectionState === 'connected' && pc.signalingState === 'stable') {
      console.log(`[Nexia WebRTC] Peer ${peerId} já está conectado e estável.`);
      return;
    }

    // Se estiver em estado transiente, desconectado ou sem resposta, recria para destravar a sincronização
    if (pc) {
      closePeerConnection(peerId);
      pc = undefined;
    }

    if (!pc) {
      pc = getOrCreatePeerConnection(peerId) || undefined;
    }
    if (!pc) return;

    localStream.value.getTracks().forEach((track) => {
      pc!.addTrack(track, localStream.value!);
    });

    prioritizeH264Codecs(pc);

    try {
      const offer = await pc.createOffer();
      let patchedSdp = patchOpusStereo(offer.sdp) || offer.sdp;
      const targetBitrate = selectedQuality.value?.maxBitrateKbps || 12000;
      patchedSdp = patchVideoBitrate(patchedSdp, targetBitrate) || patchedSdp;
      patchedSdp = prioritizeH264InSdp(patchedSdp) || patchedSdp;

      const finalOffer = {
        type: offer.type,
        sdp: patchedSdp,
      };
      await pc.setLocalDescription(finalOffer);

      sendSignalingMessage({
        type: 'offer',
        roomId: myRoomId,
        senderId: myUserId,
        targetId: peerId,
        payload: { type: finalOffer.type, sdp: finalOffer.sdp },
      });
      console.log(`[Nexia WebRTC] Oferta otimizada (H.264 Twitch Quality) enviada para ${peerId}`);
    } catch (e) {
      console.warn('[Nexia WebRTC] Erro ao criar oferta:', e);
    }
  }

  async function handleOffer(senderId: string, offer: RTCSessionDescriptionInit) {
    if (!isWebRTCSupported) return;

    try {
      let pc = peerConnections.get(senderId);
      if (pc && pc.signalingState !== 'stable') {
        closePeerConnection(senderId);
        pc = undefined;
      }

      if (!pc) {
        pc = getOrCreatePeerConnection(senderId) || undefined;
      }
      if (!pc) return;

      const existingName = currentStreamer.value?.name || participants.value.find((p) => p.id === senderId)?.name;
      const streamerName = existingName || `Amigo (${senderId.slice(-4)})`;

      currentStreamer.value = {
        id: senderId,
        name: streamerName,
        avatarUrl: getAvatarUrl(senderId),
        isStreaming: true,
        isSelf: false,
      };
      updateParticipantStreaming(senderId, true);
      isReceiving.value = true;

      prioritizeH264Codecs(pc);
      await pc.setRemoteDescription(new RTCSessionDescription(offer));

      const queued = pendingCandidates.get(senderId) || [];
      for (const cand of queued) {
        try {
          if (cand && (cand.candidate || cand.sdpMid !== undefined)) {
            await pc.addIceCandidate(new RTCIceCandidate(cand));
          }
        } catch (err) {}
      }
      pendingCandidates.delete(senderId);

      const answer = await pc.createAnswer();
      let patchedSdp = patchOpusStereo(answer.sdp) || answer.sdp;
      const targetBitrate = selectedQuality.value?.maxBitrateKbps || 12000;
      patchedSdp = patchVideoBitrate(patchedSdp, targetBitrate) || patchedSdp;
      patchedSdp = prioritizeH264InSdp(patchedSdp) || patchedSdp;

      const finalAnswer = {
        type: answer.type,
        sdp: patchedSdp,
      };
      await pc.setLocalDescription(finalAnswer);

      sendSignalingMessage({
        type: 'answer',
        roomId: myRoomId,
        senderId: myUserId,
        targetId: senderId,
        payload: { type: finalAnswer.type, sdp: finalAnswer.sdp },
      });
    } catch (err: any) {
      console.warn('[Nexia WebRTC] Erro em handleOffer:', err);
    }
  }

  async function handleAnswer(senderId: string, answer: RTCSessionDescriptionInit) {
    if (!isWebRTCSupported) return;
    try {
      const pc = peerConnections.get(senderId);
      if (pc && pc.signalingState === 'have-local-offer') {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));

        const queued = pendingCandidates.get(senderId) || [];
        for (const cand of queued) {
          try {
            if (cand && (cand.candidate || cand.sdpMid !== undefined)) {
              await pc.addIceCandidate(new RTCIceCandidate(cand));
            }
          } catch (err) {}
        }
        pendingCandidates.delete(senderId);
      }
    } catch (err: any) {
      console.warn('[Nexia WebRTC] Erro em handleAnswer:', err);
    }
  }

  async function handleCandidate(senderId: string, candidate: RTCIceCandidateInit) {
    if (!isWebRTCSupported) return;
    if (!candidate || (!candidate.candidate && candidate.sdpMid === undefined)) return;

    const pc = peerConnections.get(senderId);
    if (!pc || !pc.remoteDescription || !pc.remoteDescription.type) {
      if (!pendingCandidates.has(senderId)) {
        pendingCandidates.set(senderId, []);
      }
      pendingCandidates.get(senderId)!.push(candidate);
      return;
    }

    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (err) {}
  }

  // Iniciar Compartilhamento de Tela no Navegador
  // audioMode:
  //   'system'  = captura o áudio do sistema (padrão, inclui Discord se "Tela Inteira")
  //   'none'    = captura apenas vídeo, sem nenhum áudio
  //   'device'  = captura vídeo sem áudio do sistema + áudio separado de um dispositivo de entrada (ex: loopback de jogo)
  async function startScreenShare(audioMode: 'system' | 'none' | 'device' = 'system', audioDeviceId?: string): Promise<boolean> {
    try {
      if (currentStreamer.value && currentStreamer.value.isStreaming && currentStreamer.value.id !== myUserId) {
        lastError.value = `Não é possível transmitir: ${currentStreamer.value.name} já está transmitindo tela nesta chamada. Apenas 1 pessoa pode transmitir por vez.`;
        return false;
      }

      const preset = selectedQuality.value;
      const targetFps = preset.fps > 0 ? preset.fps : 60;
      const width = preset.width > 0 ? preset.width : undefined;
      const height = preset.height > 0 ? preset.height : undefined;

      const createConstraints = (strictFps: boolean): MediaTrackConstraints => ({
        width: width ? { ideal: width } : undefined,
        height: height ? { ideal: height } : undefined,
        frameRate: { ideal: targetFps },
        displaySurface: 'monitor',
      });

      const getDisplayMediaOptions = (withAudio: boolean, strictFps = true) => ({
        video: createConstraints(strictFps),
        audio: withAudio ? {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        } : false,
        // Garante que o navegador NUNCA fique preso na aba do próprio app
        preferCurrentTab: false,
        selfBrowserSurface: 'exclude',
        systemAudio: withAudio ? 'include' : 'exclude',
        surfaceSwitching: 'include',
        monitorTypeSurfaces: 'include',
      } as DisplayMediaStreamOptions);

      let stream: MediaStream;

      const captureScreen = async (withAudio: boolean): Promise<MediaStream> => {
        try {
          return await navigator.mediaDevices.getDisplayMedia(getDisplayMediaOptions(withAudio, true));
        } catch (strictErr: any) {
          if (strictErr?.name === 'OverconstrainedError') {
            console.warn('[Nexia WebRTC] Taxa estrita de 60fps sobrecarregou monitor, usando fallback adaptativo:', strictErr);
            return await navigator.mediaDevices.getDisplayMedia(getDisplayMediaOptions(withAudio, false));
          }
          throw strictErr;
        }
      };

      if (audioMode === 'none') {
        // Captura tela inteira sem nenhum áudio
        stream = await captureScreen(false);
      } else if (audioMode === 'device' && audioDeviceId) {
        // Captura tela sem áudio do sistema
        const screenStream = await captureScreen(false);
        // Captura áudio do dispositivo escolhido pelo usuário (ex: cable output, loopback)
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: { exact: audioDeviceId },
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false,
              channelCount: 2,
              sampleRate: 48000,
            },
          });
          // Combina vídeo da tela + áudio do dispositivo específico
          stream = new MediaStream([
            ...screenStream.getVideoTracks(),
            ...audioStream.getAudioTracks(),
          ]);
          // Encerra o áudio do screenStream (não é necessário)
          screenStream.getAudioTracks().forEach((t) => t.stop());
        } catch (audioErr) {
          console.warn('[Nexia WebRTC] Falha ao capturar áudio do dispositivo, usando stream de vídeo sem áudio:', audioErr);
          stream = screenStream;
        }
      } else {
        // Modo padrão: captura tela inteira com áudio do sistema (excluindo a aba do app)
        stream = await captureScreen(true);
      }

      // Otimização para 60fps sem micro-stutters: prioriza movimento para jogos (estilo Discord)
      stream.getVideoTracks().forEach((track) => {
        track.contentHint = 'motion';
        console.log('[Nexia WebRTC] Vídeo configurado com contentHint=motion (60 FPS contínuos)');
      });
      stream.getAudioTracks().forEach((track) => {
        track.contentHint = 'music';
        console.log('[Nexia WebRTC] Áudio configurado com contentHint=music (alta fidelidade)');
      });

      localStream.value = stream;
      isStreaming.value = true;
      updateParticipantStreaming(myUserId, true);

      stream.getTracks().forEach((track) => {
        track.onended = () => {
          console.log('[Nexia WebRTC] Compartilhamento finalizado no navegador pelo usuário.');
          stopStreaming();
        };
      });

      // Inicia gravação de contingência apenas se ainda não houver WebRTC peer conectado
      startMediaRecorder(stream);

      // Se o WebRTC for suportado no transmissor, tenta conectar com os peers da sala
      if (isWebRTCSupported) {
        participants.value.forEach((p) => {
          if (!p.isSelf) {
            createAndSendOffer(p.id);
          }
        });
      }

      return true;
    } catch (err: any) {
      console.error('[Nexia WebRTC] Erro ao iniciar compartilhamento de tela:', err);
      lastError.value = `${err.name || 'Erro'}: ${err.message || 'Falha ao acessar tela'}`;
      return false;
    }
  }


  // Iniciar Stream de Arquivo de Vídeo Local (MP4/MKV/WebM)
  async function startVideoFileStream(file: File): Promise<boolean> {
    try {
      const video = document.createElement('video');
      video.src = URL.createObjectURL(file);
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;

      await video.play();
      localVideoElementForFileStream = video;

      const stream = (video as any).captureStream ? (video as any).captureStream(selectedQuality.value.fps || 30) : (video as any).mozCaptureStream();

      localStream.value = stream;
      isStreaming.value = true;
      updateParticipantStreaming(myUserId, true);

      startMediaRecorder(stream);

      return true;
    } catch (err) {
      console.error('[Nexia WebRTC] Falha ao reproduzir arquivo de vídeo:', err);
      return false;
    }
  }

  function stopStreaming() {
    if (localStream.value) {
      localStream.value.getTracks().forEach((track) => track.stop());
      localStream.value = null;
    }
    if (localVideoElementForFileStream) {
      localVideoElementForFileStream.pause();
      localVideoElementForFileStream.src = '';
      localVideoElementForFileStream = null;
    }

    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      try {
        mediaRecorder.stop();
      } catch (e) {}
      mediaRecorder = null;
    }

    isStreaming.value = false;
    updateParticipantStreaming(myUserId, false);

    sendSignalingMessage({
      type: 'stream-stopped',
      roomId: myRoomId,
      senderId: myUserId,
    });

    peerConnections.forEach((pc) => pc.close());
    peerConnections.clear();
    pendingCandidates.clear();
  }

  function stopReceiving() {
    console.log('[Nexia WebRTC] Parando recepção e resetando player.');
    cleanupMediaSource();
    remoteStream.value = null;
    isReceiving.value = false;
    currentStreamer.value = null;
    participants.value.forEach((p) => {
      p.isStreaming = false;
    });
  }

  function requestActiveStream() {
    webrtcStatus.value = 'Requisitando transmissão ao vivo...';
    if (!mediaSource || mediaSource.readyState === 'closed') {
      isInitializing = true;
      initMediaSource(currentMimeType);
    }
    if (currentStreamer.value) {
      sendSignalingMessage({
        type: 'request-stream',
        roomId: myRoomId,
        senderId: myUserId,
        targetId: currentStreamer.value.id,
      });
    } else {
      sendSignalingMessage({
        type: 'request-stream',
        roomId: myRoomId,
        senderId: myUserId,
      });
    }
  }

  // Sincronização forçada: reinicia o pipeline MSE e WebRTC do zero.
  // Usado pelo botão "Sincronizar" quando a tela está preta ou desatualizada.
  function resyncStream() {
    console.log('[Nexia Stream] Resync forçado pelo usuário.');
    cleanupMediaSource();
    remoteStream.value = null;
    isInitializing = true;
    initMediaSource(currentMimeType, true);

    sendSignalingMessage({
      type: 'resync-stream',
      roomId: myRoomId,
      senderId: myUserId,
      ...(currentStreamer.value?.id ? { targetId: currentStreamer.value.id } : {}),
    });
    webrtcStatus.value = 'Sincronizando stream...';
  }

  function addParticipant(id: string, name: string, isSelf = false) {
    if (!participants.value.some((p) => p.id === id)) {
      participants.value = [
        ...participants.value,
        {
          id,
          name,
          avatarUrl: getAvatarUrl(id),
          isStreaming: false,
          isSelf,
        },
      ];
    }
  }

  function removeParticipant(id: string) {
    participants.value = participants.value.filter((p) => p.id !== id);
  }

  function updateParticipantStreaming(id: string, isStreaming: boolean) {
    participants.value = participants.value.map((p) =>
      p.id === id ? { ...p, isStreaming } : p
    );
  }

  function requestKeyframe() {
    sendSignalingMessage({
      type: 'request-keyframe',
      roomId: myRoomId,
      senderId: myUserId,
      ...(currentStreamer.value?.id ? { targetId: currentStreamer.value.id } : {}),
    });
  }

  return {
    isStreaming,
    isReceiving,
    currentStreamer,
    participants,
    selectedQuality,
    connectionState,
    lastError,
    webrtcStatus,
    localStream,
    remoteStream,
    streamVideoUrl,
    connectSignaling,
    startScreenShare,
    startVideoFileStream,
    stopStreaming,
    stopReceiving,
    requestActiveStream,
    resyncStream,
    requestKeyframe,
    addParticipant,
    whepPlayer,
  };
}
